import React, { useState, useEffect } from 'react';
import { X, Download, DeviceMobile } from '@phosphor-icons/react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    
    if (isPWA) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem('pwa-install-dismissed');
    if (dismissedAt) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        return; // Don't show for 24 hours after dismissal
      }
    }

    // Listen for install prompt (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show custom instructions
    if (isIOSDevice) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setShowPrompt(false);
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-80 bg-[#1a1e28] border border-[#4f9eff] rounded-lg p-4 shadow-xl z-50 animate-slide-up"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-[#8b949e] hover:text-white transition-colors"
        data-testid="pwa-dismiss"
      >
        <X size={18} />
      </button>

      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 bg-[#4f9eff]/20 rounded-lg flex items-center justify-center">
          <DeviceMobile size={24} className="text-[#4f9eff]" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-['IBM_Plex_Mono'] text-sm font-bold mb-1">
            Instalar VisionWeb
          </h3>
          
          {isIOS ? (
            <p className="text-[#8b949e] font-['Mulish'] text-xs mb-3">
              Toca <span className="text-white">Compartir</span> y luego{' '}
              <span className="text-white">"Agregar a Inicio"</span>
            </p>
          ) : (
            <p className="text-[#8b949e] font-['Mulish'] text-xs mb-3">
              Accede más rápido desde tu pantalla de inicio
            </p>
          )}

          {!isIOS && deferredPrompt && (
            <button
              onClick={handleInstall}
              className="flex items-center gap-2 px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-md font-['IBM_Plex_Mono'] text-xs font-bold hover:bg-[#4f9eff]/90 transition-colors"
              data-testid="pwa-install-button"
            >
              <Download size={14} />
              Instalar App
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
